import { useEffect, useRef, useState } from 'react';
import { getAudienceSocket } from '../hooks/useAudienceState';
import NicknameBar from '../components/NicknameBar';

interface ChallengeResultScreenProps {
  challengeId: string;
  result: 'success' | 'failure';
  variant: 'mole' | 'balance' | 'pixelart' | 'explore';
  personalAccuracy?: number;
  averageAccuracy?: number;
}

interface ConfettiPiece {
  x: number;
  y: number;
  rotation: number;
  color: string;
  size: number;
  speedY: number;
  speedX: number;
  speedRotation: number;
}

function ChallengeResultScreen({ result, variant, challengeId, personalAccuracy: initialPersonalAccuracy, averageAccuracy }: ChallengeResultScreenProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const confettiRef = useRef<ConfettiPiece[]>([]);
  const animationRef = useRef<number>(0);
  const [personalAccuracy, setPersonalAccuracy] = useState<number | undefined>(initialPersonalAccuracy);

  // Ascolta risultato personale pixelart
  useEffect(() => {
    if (variant !== 'pixelart') return;
    const socket = getAudienceSocket();
    if (!socket) return;

    const handleUserResult = (data: { challengeId: string; personalAccuracy: number }) => {
      if (data.challengeId === challengeId) {
        setPersonalAccuracy(data.personalAccuracy);
      }
    };

    socket.on('challenge:user-result', handleUserResult);
    return () => {
      socket.off('challenge:user-result', handleUserResult);
    };
  }, [variant, challengeId]);

  // Confetti per successo
  useEffect(() => {
    if (result !== 'success') return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const colors = [
      '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff',
      '#ff8800', '#ff0088', '#88ff00', '#0088ff', '#8800ff', '#00ff88',
    ];

    const pieces: ConfettiPiece[] = [];
    for (let i = 0; i < 150; i++) {
      pieces.push({
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * canvas.height,
        rotation: Math.random() * 360,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 8 + Math.random() * 12,
        speedY: 2 + Math.random() * 4,
        speedX: -2 + Math.random() * 4,
        speedRotation: -5 + Math.random() * 10,
      });
    }
    confettiRef.current = pieces;

    const animate = () => {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      confettiRef.current.forEach((piece) => {
        piece.y += piece.speedY;
        piece.x += piece.speedX;
        piece.rotation += piece.speedRotation;

        if (piece.y > canvas.height + 20) {
          piece.y = -20;
          piece.x = Math.random() * canvas.width;
        }

        ctx.save();
        ctx.translate(piece.x, piece.y);
        ctx.rotate((piece.rotation * Math.PI) / 180);
        ctx.fillStyle = piece.color;
        ctx.fillRect(-piece.size / 2, -piece.size / 4, piece.size, piece.size / 2);
        ctx.restore();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationRef.current);
    };
  }, [result]);

  if (result === 'success') {
    return (
      <div className="relative flex flex-col items-center justify-center min-h-screen p-4 pt-10 overflow-hidden parchment-bg parchment-bg-green">
        <NicknameBar />

        <canvas
          ref={canvasRef}
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 10 }}
        />

        <div className="relative z-20 text-center">
          <div className="text-8xl mb-6 animate-bounce">&#127881;</div>
          <h1 className="text-4xl font-display font-bold text-team-green mb-4 animate-pulse">
            PROVA SUPERATA!
          </h1>
          {variant === 'pixelart' ? (
            <div>
              {personalAccuracy !== undefined && (
                <p className="text-2xl text-ink-light mb-2 font-heading">
                  La tua precisione: <strong>{personalAccuracy}%</strong>
                </p>
              )}
              {averageAccuracy !== undefined && (
                <p className="text-lg text-ink-light font-body">
                  Media: {Math.round(averageAccuracy)}%
                </p>
              )}
            </div>
          ) : (
            <p className="text-xl text-ink font-heading">
              Ottimo lavoro!
            </p>
          )}
        </div>
      </div>
    );
  }

  // Fallimento - stile GTA Wasted
  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen p-4 pt-10 overflow-hidden parchment-bg">
      <NicknameBar />

      {/* Overlay rosso */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(139, 0, 0, 0.4), rgba(50, 0, 0, 0.6))',
          filter: 'grayscale(0.5)',
        }}
      />

      <div className="relative z-20 text-center">
        <h1
          className="text-5xl font-display font-black text-team-red mb-4 tracking-widest"
          style={{
            textShadow: '0 0 20px rgba(220, 38, 38, 0.5), 0 0 40px rgba(220, 38, 38, 0.3)',
            animation: 'wastedZoom 0.8s ease-out forwards',
          }}
        >
          PROVA FALLITA
        </h1>
        {variant === 'pixelart' ? (
          <div>
            {personalAccuracy !== undefined && (
              <p className="text-xl text-ink-light mb-2 font-heading">
                La tua precisione: <strong>{personalAccuracy}%</strong>
              </p>
            )}
            {averageAccuracy !== undefined && (
              <p className="text-lg text-ink-light font-body">
                Media: {Math.round(averageAccuracy)}%
              </p>
            )}
          </div>
        ) : (
          <p className="text-xl text-ink-light font-heading">
            Non abbastanza...
          </p>
        )}
      </div>

      <style>{`
        @keyframes wastedZoom {
          0% { transform: scale(3); opacity: 0; }
          60% { transform: scale(0.9); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default ChallengeResultScreen;
